import React from 'react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/Skeleton';
import { Sunrise, ArrowRight, Sparkles, RefreshCw, TrendingUp, AlertTriangle, Trophy } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

function formatRelativeTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

const DailyBriefingTab = ({ briefings, projects, loading, onRefresh }) => {
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 3 }).map((_, index) => (
          <Card key={index} className="rounded-[24px] p-6">
            <Skeleton className="h-6 w-3/4 mb-4" />
            <Skeleton className="h-4 w-full mb-2" />
            <Skeleton className="h-4 w-5/6 mb-4" />
            <div className="flex gap-2 mt-4">
              <Skeleton className="h-6 w-20" />
              <Skeleton className="h-6 w-24" />
            </div>
          </Card>
        ))}
      </div>
    );
  }

  if (!briefings || briefings.length === 0) {
    return (
      <Card className="rounded-[24px] p-12 text-center glass-subtle">
        <div className="flex flex-col items-center gap-4 max-w-md mx-auto">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500/20 to-pink-500/20 flex items-center justify-center">
            <Sunrise className="w-8 h-8 text-orange-500" />
          </div>
          <h3 className="text-xl font-bold">No briefings yet</h3>
          <p className="text-muted-foreground">
            Generate your first daily briefing to start tracking project progress and insights.
          </p>
        </div>
      </Card>
    );
  }

  const getProjectColor = (projectId) => {
    const project = projects?.find((p) => p.id === projectId);
    return project?.color || 'default';
  };

  return (
    <div className="space-y-4">
      {briefings.map((briefing, index) => {
        const insightSummary = briefing.insight_summary;

        return (
          <Card
            key={briefing.key}
            className="rounded-[24px] p-6 glass-elevated animate-fade-up cursor-pointer hover:shadow-glass-lg hover:-translate-y-0.5 transition-all duration-300"
            style={{ animationDelay: `${index * 60}ms` }}
            onClick={() => navigate(`/projects/${briefing.project_id}/briefings/${briefing.key}`)}
          >
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500/20 to-purple-500/20 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-violet-500" />
              </div>
              <div className="flex-1 min-w-0 space-y-2">
                {/* Header */}
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-lg font-bold">Daily Summary</h3>
                    <Badge variant="glass" color={getProjectColor(briefing.project_id)}>
                      {briefing.project_name || 'Project'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {formatRelativeTime(briefing.generated_at)}
                    </span>
                    {onRefresh && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 rounded-full"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRefresh(briefing.project_id);
                        }}
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Narrative */}
                {briefing.narrative && (
                  <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
                    {briefing.narrative}
                  </p>
                )}

                {/* Insight summary badges */}
                {insightSummary && (
                  <div className="flex items-center gap-3 pt-1">
                    {insightSummary.goal_update_count > 0 && (
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <TrendingUp className="w-3.5 h-3.5 text-blue-500" />
                        {insightSummary.goal_update_count} goal{insightSummary.goal_update_count !== 1 ? 's' : ''} updated
                      </span>
                    )}
                    {insightSummary.achievement_count > 0 && (
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Trophy className="w-3.5 h-3.5 text-amber-500" />
                        {insightSummary.achievement_count} achievement{insightSummary.achievement_count !== 1 ? 's' : ''}
                      </span>
                    )}
                    {insightSummary.risk_count > 0 && (
                      <span className={cn(
                        'flex items-center gap-1 text-xs',
                        insightSummary.has_critical_risk ? 'text-red-500 font-medium' : 'text-muted-foreground'
                      )}>
                        <AlertTriangle className={cn(
                          'w-3.5 h-3.5',
                          insightSummary.has_critical_risk ? 'text-red-500' : 'text-orange-500'
                        )} />
                        {insightSummary.risk_count} risk{insightSummary.risk_count !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                )}

                {/* Progress summary from insights */}
                {insightSummary?.progress_summary && (
                  <p className="text-xs text-muted-foreground/80 italic">
                    {insightSummary.progress_summary}
                  </p>
                )}

                {/* Read more */}
                <div className="flex items-center gap-1 text-sm font-medium text-primary pt-1">
                  View details
                  <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
};

export default DailyBriefingTab;
